import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  type User,
  type Gift,
  type WizardState,
  type ExperienceDataMap,
  type UploadedPhoto,
  Plan,
  Occasion,
  PrivacyMode,
  RecipientType,
} from "@/types";
import { saveToken, clearToken } from "@/lib/api";

// ─── Auth Store ────────────────────────────────────────────────────────────────

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  // hasHydrated marca que o store foi reidratado do localStorage
  // (Zustand persist) E que o AuthSync rodou uma vez validando o token.
  // Route guards usam isso pra não redirecionar antes da hidratação.
  hasHydrated: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string) => void;
  setHasHydrated: (v: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isLoading: false,
      hasHydrated: false,
      setUser: (user) => set({ user }),
      setToken: (token) => {
        saveToken(token);
        set({ token });
      },
      setHasHydrated: (v) => set({ hasHydrated: v }),
      logout: () => {
        clearToken();
        set({ user: null, token: null });
      },
    }),
    {
      name: "lovepage-auth-v1",
      storage: createJSONStorage(() =>
        typeof window !== "undefined"
          ? localStorage
          : ({
              getItem: () => null,
              setItem: () => {},
              removeItem: () => {},
              clear: () => {},
              key: () => null,
              length: 0,
            } as unknown as Storage)
      ),
      // Persiste só o user — o token vive no localStorage (lovepage_token),
      // gerenciado pelo lib/api.ts. hasHydrated NUNCA é persistido — começa
      // sempre como false e é setado true pelo AuthSync no mount.
      partialize: (state) => ({ user: state.user }),
    }
  )
);

// ─── Wizard Store ──────────────────────────────────────────────────────────────

const DEFAULT_WIZARD: WizardState = {
  step: 0,
  coupleData: {
    recipientName: "",
    partnerName: "",
    coupleStartDate: "",
    // Default romântico-clássico do produto: Dia dos Namorados é o cenário
    // mais comum de uso, então abrimos o wizard com essa ocasião já
    // pré-selecionada (o usuário pode trocar à vontade).
    occasion: Occasion.VALENTINES_DAY,
    title: "",
    slug: "",
  },
  letter: {
    loveLetter: "",
  },
  photos: [],
  music: {
    musicUrl: "",
    musicName: "",
    artistName: "",
  },
  theme: {
    // Único layout principal hoje é o Spotify; "purple-romantic" é o tema
    // padrão (ver SPOTIFY_THEME_SLUGS em PublicGiftRenderer).
    templateSlug: "purple-romantic",
    customBackgroundUrl: undefined,
    customBackgroundOpaque: true,
  },
  privacy: {
    privacyMode: PrivacyMode.PUBLIC,
    password: "",
  },
  plan: Plan.PREMIUM,
  selectedExperiences: [],
  experienceData: {},
};

interface WizardStoreState extends WizardState {
  giftId: string | null;
  // Identificador do rascunho que aparece na URL (/criar/<draftId>).
  // Antes do checkout é um `lp_…` gerado no client. Após `createGift`,
  // passa a ser o `gift.id` real (`gft_…`) — assim o URL acompanha o
  // ciclo de vida do rascunho até virar pagamento.
  draftId: string | null;
  setDraftId: (id: string | null) => void;
  // Quando o usuário pula direto pra um step específico (ex.: clica em
  // "Preencher Carta" no Resumo), guardamos aqui pra onde ele DEVE voltar
  // se apertar Voltar. Sem isso, o handleBack faria prevStep() sequencial
  // e mandaria pro step 2 quando o esperado era o Resumo (9).
  returnToStep: number | null;
  setReturnToStep: (step: number | null) => void;
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setRecipientType: (type: RecipientType) => void;
  setCoupleData: (data: Partial<WizardState["coupleData"]>) => void;
  setLetter: (data: Partial<WizardState["letter"]>) => void;
  setPhotos: (photos: UploadedPhoto[]) => void;
  setMusic: (data: Partial<WizardState["music"]>) => void;
  setTheme: (data: Partial<WizardState["theme"]>) => void;
  setPrivacy: (data: Partial<WizardState["privacy"]>) => void;
  setPlan: (plan: Plan) => void;
  setGiftId: (id: string) => void;
  addExperience: (id: string) => void;
  removeExperience: (id: string) => void;
  toggleExperience: (id: string) => void;
  setSelectedExperiences: (ids: string[]) => void;
  setExperienceData: (id: string, data: Record<string, unknown>) => void;
  updateExperienceData: (id: string, partial: Record<string, unknown>) => void;
  removeExperienceData: (id: string) => void;
  replaceAllExperienceData: (data: ExperienceDataMap) => void;
  reset: () => void;
}

// Campos que são serializáveis e devem ser persistidos. Antes `photos`
// era File[] e ficava de fora — depois do refactor pra upload imediato,
// `photos: UploadedPhoto[]` (só strings) é serializável e ENTRA no
// persist, o que faz as fotos sobreviverem reload no meio do wizard.
type PersistedWizard = Omit<
  WizardStoreState,
  | "setStep"
  | "nextStep"
  | "prevStep"
  | "setRecipientType"
  | "setCoupleData"
  | "setLetter"
  | "setPhotos"
  | "setMusic"
  | "setTheme"
  | "setPrivacy"
  | "setPlan"
  | "setGiftId"
  | "setDraftId"
  | "setReturnToStep"
  | "addExperience"
  | "removeExperience"
  | "toggleExperience"
  | "setSelectedExperiences"
  | "setExperienceData"
  | "updateExperienceData"
  | "removeExperienceData"
  | "replaceAllExperienceData"
  | "reset"
>;

export const useWizardStore = create<WizardStoreState>()(
  persist(
    (set) => ({
      ...DEFAULT_WIZARD,
      giftId: null,
      draftId: null,
      returnToStep: null,
      setDraftId: (id) => set({ draftId: id }),
      setReturnToStep: (step) => set({ returnToStep: step }),
      // setStep direto sempre limpa o returnToStep — quem quiser ativar
      // o "voltar pro Resumo" precisa chamar setReturnToStep explicitamente
      // logo depois (StepPreview faz isso).
      setStep: (step) => set({ step, returnToStep: null }),
      // nextStep tem dois comportamentos:
      // - Modo normal (returnToStep == null): avança step+1, lineares.
      // - Modo "edição inline a partir do Resumo" (returnToStep != null):
      //   o usuário foi pra esse step a partir do Resumo via
      //   `setReturnToStep(9)`. "Continuar" agora deve VOLTAR direto
      //   pra etapa do Resumo (em vez de obrigar a passar por todos os
      //   forms seguintes que ele já preencheu). Limpa returnToStep
      //   pra esgotar esse contexto.
      nextStep: () =>
        set((s) => {
          if (s.returnToStep !== null && s.step !== s.returnToStep) {
            return { step: s.returnToStep, returnToStep: null };
          }
          return { step: Math.min(s.step + 1, 10), returnToStep: null };
        }),
      // prevStep tradicional só decrementa um passo. O atalho pra Resumo
      // (quando returnToStep está ativo) é tratado no WizardContent —
      // ele chama setStep(returnToStep) diretamente.
      prevStep: () => set((s) => ({ step: Math.max(s.step - 1, 0) })),
      setRecipientType: (type) => set({ recipientType: type }),
      setCoupleData: (data) =>
        set((s) => ({ coupleData: { ...s.coupleData, ...data } })),
      setLetter: (data) => set((s) => ({ letter: { ...s.letter, ...data } })),
      setPhotos: (photos) => set({ photos }),
      setMusic: (data) => set((s) => ({ music: { ...s.music, ...data } })),
      setTheme: (data) => set((s) => ({ theme: { ...s.theme, ...data } })),
      setPrivacy: (data) =>
        set((s) => ({ privacy: { ...s.privacy, ...data } })),
      setPlan: (plan) => set({ plan }),
      setGiftId: (id) => set({ giftId: id }),
      addExperience: (id) =>
        set((s) =>
          s.selectedExperiences.includes(id)
            ? s
            : { selectedExperiences: [...s.selectedExperiences, id] }
        ),
      removeExperience: (id) =>
        set((s) => ({
          selectedExperiences: s.selectedExperiences.filter((x) => x !== id),
        })),
      toggleExperience: (id) =>
        set((s) => ({
          selectedExperiences: s.selectedExperiences.includes(id)
            ? s.selectedExperiences.filter((x) => x !== id)
            : [...s.selectedExperiences, id],
        })),
      setSelectedExperiences: (ids) => set({ selectedExperiences: ids }),
      setExperienceData: (id, data) =>
        set((s) => ({
          experienceData: { ...s.experienceData, [id]: data },
        })),
      updateExperienceData: (id, partial) =>
        set((s) => ({
          experienceData: {
            ...s.experienceData,
            [id]: { ...(s.experienceData[id] ?? {}), ...partial },
          },
        })),
      removeExperienceData: (id) =>
        set((s) => {
          if (!(id in s.experienceData)) return s;
          const next = { ...s.experienceData };
          delete next[id];
          return { experienceData: next };
        }),
      replaceAllExperienceData: (data) => set({ experienceData: data }),
      reset: () =>
        set({
          ...DEFAULT_WIZARD,
          giftId: null,
          draftId: null,
          photos: [],
          selectedExperiences: [],
          experienceData: {},
        }),
    }),
    {
      name: "lovepage-wizard-v5",
      storage: createJSONStorage(() =>
        typeof window !== "undefined"
          ? localStorage
          : ({
              getItem: () => null,
              setItem: () => {},
              removeItem: () => {},
              clear: () => {},
              key: () => null,
              length: 0,
            } as unknown as Storage)
      ),
      // Persiste tudo (incluindo photos: UploadedPhoto[]) — só funções
      // são excluídas via Omit no PersistedWizard. EXCETO returnToStep:
      // é UI transitória ("o usuário acabou de clicar em editar do
      // Resumo, devolve ele pra lá quando terminar"). Se persistisse,
      // sobreviveria a um close+reopen do navegador e o próximo
      // nextStep pularia pro Resumo do nada — exatamente o bug
      // reportado: criar do zero acabava só com título salvo.
      partialize: (state): PersistedWizard => ({
        step: state.step,
        recipientType: state.recipientType,
        coupleData: state.coupleData,
        letter: state.letter,
        photos: state.photos,
        music: state.music,
        theme: state.theme,
        privacy: state.privacy,
        plan: state.plan,
        giftId: state.giftId,
        draftId: state.draftId,
        returnToStep: null,
        selectedExperiences: state.selectedExperiences,
        experienceData: state.experienceData,
      }),
      // Limpa o returnToStep no boot — protege quem já tem
      // localStorage poluído de versões anteriores que persistiam o
      // valor. Sem isso, na primeira ação o nextStep teleportaria
      // direto pro Resumo deixando tudo intermediário vazio.
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.returnToStep = null;
        }
      },
    }
  )
);

// ─── Dashboard Store ───────────────────────────────────────────────────────────

interface DashboardState {
  gifts: Gift[];
  isLoading: boolean;
  setGifts: (gifts: Gift[]) => void;
  setLoading: (v: boolean) => void;
  removeGift: (id: string) => void;
}

export const useDashboardStore = create<DashboardState>()((set) => ({
  gifts: [],
  isLoading: false,
  setGifts: (gifts) => set({ gifts }),
  setLoading: (isLoading) => set({ isLoading }),
  removeGift: (id) =>
    set((s) => ({ gifts: s.gifts.filter((g) => g.id !== id) })),
}));

// ─── Toast Store ───────────────────────────────────────────────────────────────

export type ToastType = "success" | "error" | "info";

interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
}

interface ToastState {
  toasts: ToastMessage[];
  addToast: (msg: Omit<ToastMessage, "id">) => void;
  removeToast: (id: string) => void;
}

export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],
  addToast: (msg) => {
    const id = `toast_${Date.now()}`;
    set((s) => ({ toasts: [...s.toasts, { ...msg, id }] }));
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    }, 4000);
  },
  removeToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));
